import { useState, useEffect } from 'react';
import { useLocalStore } from 'mobx-react';
import { stomp as stompModel, rtc } from '@/models';
import { toJS } from 'mobx';
import useHostOperation, { HostOperationResp } from './useHostOperation';
import useHostChange, { HostChangeResp } from './useHostChange';
import useCoHostChange, { CoHostChangeResp } from './useCoHostChange';
import useTitleChange, { TitleChangeResp } from './useTitleChange';
import useShareScreenStateChange, { ShareScreenStateChangeResp } from './useShareScreenStateChange';
import useForceCloseVideo from './useForceCloseVideo';
import useMeetingFinish from './useMeetingFInish';
import useChatInMeeting from './useChatInMeeting';
import useWhiteboardPermission, { WhiteboardPermissionResp } from './useWhiteboardPermission';
import useWhiteboardInfoChange, { WhiteboardInfoResp } from './useWhiteboardInfo';
import useCodeEditorStateChange, { CodeEditorStateChangeResp } from './useCodeEditorStateChange';
import useRecordingStateChange, { RecordingStateChangeResp } from './useRecordingStateChange';
import useRecordingReject, { RecordingRejectResp } from './useRecordingReject';
import useInterpretationStatusChange, {
  InterpretationStatusChangeResp,
} from './useInterpretationStatusChange';
import useInterpreterAudioStatusChange, {
  InterpreterAudioStatusChangeResp,
} from './useInterpreterAudioStatusChange';
import useInterpreterOutputChannelChange, {
  InterpreterOutputChannelChangeResp,
} from './useInterpreterOutputChannelChange';
import useAppsStateChange from './useAppsStateChange';
import useTranscribeChange, { transcriptionData } from './useTranscribeChange';
import useHandUpChange, { handupChangeData } from './handUpChange';
import useReactionChange, { ReactionChangeChangeData } from './reactionChange';
import useHostTranscribeChange, { hostTranscriptionData } from './useHostTranscribeChange';
import { HandUpStatus } from '../../component/EmoJ';
import useFocusedChange, { FocusedChangeResp } from './useFocusedChange';
import useNotePermissionChange, { NotePermissionChangeResp } from './useNotePermissionChange';
import useNoteStatusChange, { NoteStatusChangeResp } from './useNoteStatusChange';
import useScreenSizeChange, { ScreenSizeChangeResp } from './useScreenSizeChange';

interface Params {
  meetingId: string;
}

export default function useStompResponse(params: Params) {
  const { meetingId } = params;
  const { stomp, rtc: RTCInstance } = useLocalStore(() => {
    return {
      stomp: stompModel,
      rtc,
    };
  });

  const hostOperation = useHostOperation();
  const hostChange = useHostChange();
  const titleChange = useTitleChange();
  const shareScreenStateChange = useShareScreenStateChange();
  const forceCloseVideo = useForceCloseVideo();
  const logout = useMeetingFinish();
  const toggleChatBtnDisableStatus = useChatInMeeting();
  const setWhiteboardPermission = useWhiteboardPermission();
  const whiteboardInfoChange = useWhiteboardInfoChange();
  const codeEditorStateChange = useCodeEditorStateChange();
  const recordingStateChange = useRecordingStateChange();
  const recordingReject = useRecordingReject();
  const interpretationStatusChange = useInterpretationStatusChange();
  const interpreterAudioStatusChange = useInterpreterAudioStatusChange();
  const interpreterOutputChannelChange = useInterpreterOutputChannelChange();
  const transcribeChange = useTranscribeChange();
  const hostTranscribeChange = useHostTranscribeChange();
  const appsStateChange = useAppsStateChange();
  const handupChange = useHandUpChange();
  const reactionChange = useReactionChange();
  const focusedChange = useFocusedChange();
  const coHostChange = useCoHostChange();
  const notePermissionChange = useNotePermissionChange();
  const noteStatusChange = useNoteStatusChange();
  const screenSizeChange = useScreenSizeChange();

  useEffect(() => {
    if (stomp.responseBody) {
      const body: { channelId: number; uid: string; state: number } = stomp.responseBody.data;
      console.log(stomp.responseBody.commandId, body);
      console.log(toJS(stomp.responseBody), 'stomp.responseBody change');
      if (body.channelId !== RTCInstance.cid) {
        // 消息的会议和当前会议不是同一个时，丢弃
        return;
      }
      switch (stomp.responseBody.commandId) {
        // 被主持人操作(麦克风和视频)
        case 103:
          hostOperation(body as HostOperationResp);
          break;
        // 主持人变更
        case 104:
          console.log(body, 'body');
          hostChange(body as typeof body & HostChangeResp);
          break;
        // 更新会议标题
        case 108:
          titleChange(body as typeof body & TitleChangeResp);
          break;
        // 屏幕共享信息
        case 116:
          shareScreenStateChange(body as typeof body & ShareScreenStateChangeResp);
          break;
        // 客户端视频被强制关闭
        case 123:
          forceCloseVideo();
          break;
        // 被主持人踢出会议
        case 126:
          logout(meetingId);
          break;
        // 主持人开关会议内聊天
        case 127:
          toggleChatBtnDisableStatus(body);
          break;
        // 白板开启权限
        case 131:
          setWhiteboardPermission(body as typeof body & WhiteboardPermissionResp);
          break;
        // 白板状态信息变更(开启或关闭)
        case 132:
          whiteboardInfoChange(body as typeof body & WhiteboardInfoResp);
          break;
        // 代码编辑器状态信息变更
        case 138:
          codeEditorStateChange(body as typeof body & CodeEditorStateChangeResp);
          break;
        // 会议内三方应用状态变更（白板、代码编辑器）
        case 200:
          appsStateChange();
          break;
        // 录制状态变更
        case 1401:
          recordingStateChange(body as typeof body & RecordingStateChangeResp);
          break;
        // 主持人拒绝开启录制
        case 1403:
          recordingReject(body as typeof body & RecordingRejectResp);
          break;
        // 同声传译配置变更（包括是否开启同传，译员状态
        case 1501:
          interpretationStatusChange(
            body as typeof body & InterpretationStatusChangeResp,
            meetingId
          );
          break;
        // 译员麦克风状态变更
        case 1506:
          interpreterAudioStatusChange(body as typeof body & InterpreterAudioStatusChangeResp);
          break;
        // 译员切换频道通知
        case 1507:
          interpreterOutputChannelChange(body as typeof body & InterpreterOutputChannelChangeResp);
          break;
        // 转录功能状态变更
        case 1301:
          transcribeChange(body as typeof body & transcriptionData);
          break;
        case 1602:
          // commandId=1602，参会人发送表情时，向所有参会人发送此通知。
          reactionChange(body as typeof body & ReactionChangeChangeData);
          break;
        case 1603:
          // commandId=1603，参会设备举手时，向所有参会设备发送此通知
          handupChange(body as typeof body & handupChangeData, HandUpStatus.on);
          break;
        case 1604:
          // commandId=1604，两个场景会发送此通知，发送给所有参会设备：主持人放下参会人的举手时；已举手的参会设备放下举手时。
          handupChange(body as typeof body & handupChangeData, HandUpStatus.off);
          break;
        //主持人处理转录申请
        case 1303:
          hostTranscribeChange(body as typeof body & hostTranscriptionData);
          break;
        //开启专注处理
        case 128:
          focusedChange(body as typeof body & FocusedChangeResp);
          break;
        // 联席主持人变更
        case 1005:
          coHostChange(body as typeof body & CoHostChangeResp);

        //共享屏幕批注权限变更
        case 1105:
          notePermissionChange(body as typeof body & NotePermissionChangeResp);
          break;

        //开启关闭批注通知
        case 1106:
          noteStatusChange(body as typeof body & NoteStatusChangeResp);
          break;

        //共享方共享源大小变更通知
        case 1108:
          screenSizeChange(body as typeof body & ScreenSizeChangeResp);
          break;
        default:
          console.log('未知ws状态：', stomp.responseBody);
          break;
      }
    }
  }, [stomp.responseBody]);

  return useStompResponse;
}
